import {useContext} from 'react';

import {useNavigate} from 'react-router-dom';

import {useCultureAgentResults} from '../hooks/useCultureAgentResults';
import type {CultureAgentResultsConfig, CultureAgentResultsLabels} from '../types';

import {CompanyMatching} from './CompanyMatching';
import {CultureReport} from './CultureReport';
import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';

import './CultureAgentResults.css';

export interface CultureAgentResultsProps {
  config: CultureAgentResultsConfig;
  labels: CultureAgentResultsLabels;
}

export const CultureAgentResults = ({config, labels}: CultureAgentResultsProps) => {
  const language = useContext(LanguageContext);
  const navigate = useNavigate();
  const {surveyData, loading, error, getCompanyMatch, isMatchLimitReached} = useCultureAgentResults(config);

  const handleCreateAccount = () => {
    navigate(`/talent/sign-up/${config.sessionId ?? ''}?flow=chat`, {
      state: {chatGetResultsPayload: surveyData ?? undefined},
    });
  };

  if (loading) {
    return (
      <div className='culture-agent-results-wrapper'>
        <div className='culture-agent-results'>
          <div className='culture-agent-results__loading'>
            <span className='culture-agent-results__loading-text'>{labels.loading}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='culture-agent-results-wrapper'>
        <div className='culture-agent-results'>
          <div className='culture-agent-results__error'>{error.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className='culture-agent-results-wrapper'>
      <div className='culture-agent-results'>
        <div className='culture-agent-results__content'>
          <CultureReport
            cultureTypeName={surveyData?.culture_type_name ?? ''}
            cultureTypeInfo={surveyData?.culture_type_info}
            traitApproximations={surveyData?.trait_approximations ?? []}
            locale={language}
          />
          <CompanyMatching
            heading={labels.companyMatchHeading}
            description={labels.companyMatchDescription}
            inputPlaceholder={labels.companyMatchInputPlaceholder}
            matchResultHeading={labels.companyMatchResultHeading}
            youLabel={labels.youLabel}
            locale={language}
            matchLimitReached={isMatchLimitReached}
            loadingMessages={labels.loadingMessages}
            onGetMatch={getCompanyMatch}
          />
        </div>
      </div>
      <div className='culture-agent-results__actions'>
        <a
          href={process.env.REACT_APP_JOBS_BASE_URL ?? '#'}
          target='_blank'
          rel='noopener noreferrer'
          className='culture-agent-results__action-btn'
        >
          {labels.discoverMatchingJobs}
        </a>
        <span className='culture-agent-results__actions-or'>{labels.or}</span>
        <button type='button' className='culture-agent-results__action-btn' onClick={handleCreateAccount}>
          {labels.createAccount}
        </button>
      </div>
    </div>
  );
};
